import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';

const RestaurantOrders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [pastOrders, setPastOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedTab, setSelectedTab] = useState('active'); // active, past

  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchNotifications();
      
      // Poll for updates every 15 seconds
      const interval = setInterval(() => {
        fetchOrders();
        fetchNotifications();
      }, 15000);
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // Separate orders into active and past
    const active = orders.filter(order => 
      ['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status)
    );
    const past = orders.filter(order => 
      ['completed', 'cancelled'].includes(order.status)
    );
    
    setActiveOrders(active);
    setPastOrders(past);
  }, [orders]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurant_orders')
        .select(`
          *,
          order_items (
            *,
            menu_item:menu_items (
              name,
              description,
              price
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      // Get all order IDs for this user
      const { data: userOrders } = await supabase
        .from('restaurant_orders')
        .select('id')
        .eq('user_id', user.id);

      if (!userOrders || userOrders.length === 0) return;

      const orderIds = userOrders.map(order => order.id);

      const { data, error } = await supabase
        .from('order_notifications')
        .select(`
          *,
          order:restaurant_orders (
            order_number,
            table_number
          )
        `)
        .in('order_id', orderIds)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await supabase
        .from('order_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
      
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      
      if (unreadIds.length > 0) {
        await supabase
          .from('order_notifications')
          .update({ is_read: true })
          .in('id', unreadIds);
        
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const sendCustomerNotification = async (orderId) => {
    const message = prompt('Enter your message to the kitchen:');
    if (!message) return;

    try {
      await supabase
        .from('order_notifications')
        .insert([{
          order_id: orderId,
          sender_type: 'customer',
          message: message,
          is_read: false
        }]);
      
      alert('Message sent to kitchen');
      fetchNotifications();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send message');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ff9800';
      case 'confirmed': return '#2196F3';
      case 'preparing': return '#9c27b0';
      case 'ready': return '#4CAF50';
      case 'served': return '#8BC34A';
      case 'completed': return '#4CAF50';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return '⏳';
      case 'confirmed': return '✓';
      case 'preparing': return '🍳';
      case 'ready': return '✅';
      case 'served': return '🍽';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (!user) {
    return (
      <div className="page-container">
        <div className="page-wrapper">
          <h1 className="page-title">Please Login</h1>
          <p>You need to be logged in to view your orders.</p>
          <button 
            onClick={() => navigate('/login')}
            className="cta-button"
            style={{ marginTop: '1rem' }}
          >
            Go to Login
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button 
            onClick={() => navigate('/prive-lounge')} 
            className="back-button"
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--gold)',
              color: 'var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>

          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              position: 'relative',
              padding: '0.5rem 1rem',
              background: showNotifications ? 'var(--gold)' : 'transparent',
              border: '1px solid var(--gold)',
              color: showNotifications ? 'black' : 'var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔔 Notifications
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                background: 'red',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem'
              }}>
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        <h1 className="page-title">My Orders</h1>
        <div className="underline"></div>

        {/* Notifications Panel */}
        {showNotifications && (
          <div className="card" style={{ marginBottom: '2rem', maxHeight: '400px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--gold)' }}>Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllNotificationsRead}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: '1px solid var(--gold)',
                    color: 'var(--gold)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Mark All Read
                </button>
              )}
            </div>
            
            {notifications.length === 0 ? (
              <p style={{ opacity: 0.6, textAlign: 'center', padding: '2rem' }}>No notifications</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {notifications.map(notif => (
                  <div
                    key={notif.id}
                    onClick={() => !notif.is_read && markNotificationRead(notif.id)}
                    style={{
                      padding: '1rem',
                      background: notif.is_read ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 215, 0, 0.1)',
                      borderRadius: '4px',
                      border: notif.is_read ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gold)',
                      cursor: notif.is_read ? 'default' : 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                        Order #{notif.order?.order_number}
                      </strong>
                      <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                        {new Date(notif.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p style={{ margin: 0 }}>{notif.message}</p>
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                      From: {notif.sender_type}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab Selection */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
          <button
            onClick={() => setSelectedTab('active')}
            style={{
              padding: '0.75rem 1.5rem',
              background: selectedTab === 'active' ? 'var(--gold)' : 'transparent',
              color: selectedTab === 'active' ? 'black' : 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: selectedTab === 'active' ? 'bold' : 'normal'
            }}
          >
            Active Orders ({activeOrders.length})
          </button>
          <button
            onClick={() => setSelectedTab('past')}
            style={{
              padding: '0.75rem 1.5rem',
              background: selectedTab === 'past' ? 'var(--gold)' : 'transparent',
              color: selectedTab === 'past' ? 'black' : 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: selectedTab === 'past' ? 'bold' : 'normal'
            }}
          >
            Past Orders ({pastOrders.length})
          </button>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div>
            {/* Active Orders */}
            {selectedTab === 'active' && (
              <>
                {activeOrders.length === 0 ? (
                  <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <h3 style={{ color: 'var(--gold)' }}>No active orders</h3>
                    <p style={{ opacity: 0.6, marginTop: '0.5rem', marginBottom: '1.5rem' }}>
                      Your active orders will appear here
                    </p>
                    <button
                      onClick={() => navigate('/restaurant-order')}
                      className="cta-button"
                    >
                      Order Now
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {activeOrders.map(order => (
                      <div key={order.id} className="card" style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                          <div>
                            <h3 style={{ color: 'var(--gold)', margin: '0 0 0.5rem 0' }}>
                              Order #{order.order_number}
                            </h3>
                            <p style={{ margin: '0 0 0.25rem 0', opacity: 0.7 }}>
                              Table {order.table_number}
                            </p>
                            <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
                              {new Date(order.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div style={{
                            padding: '0.5rem 1rem',
                            background: getStatusColor(order.status),
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span>{getStatusIcon(order.status)}</span>
                            <span>{order.status}</span>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div style={{ marginBottom: '1rem' }}>
                          {order.order_items.map(item => (
                            <div 
                              key={item.id}
                              style={{ 
                                padding: '0.75rem',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '4px',
                                marginBottom: '0.5rem'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <strong>{item.quantity}x {item.menu_item?.name}</strong>
                                <span style={{ color: 'var(--gold)' }}>
                                  £{(item.price * item.quantity).toFixed(2)}
                                </span>
                              </div>
                              {item.special_instructions && (
                                <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0.25rem 0 0', fontStyle: 'italic' }}>
                                  Note: {item.special_instructions}
                                </p>
                              )}
                              <div style={{
                                fontSize: '0.75rem',
                                marginTop: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                background: getStatusColor(item.status),
                                color: 'white',
                                borderRadius: '4px',
                                display: 'inline-block'
                              }}>
                                {item.status}
                              </div>
                            </div>
                          ))}
                        </div>

                        {order.special_instructions && (
                          <div style={{
                            padding: '0.75rem',
                            background: 'rgba(255, 235, 59, 0.1)',
                            borderRadius: '4px',
                            marginBottom: '1rem',
                            border: '1px solid #ffeb3b'
                          }}>
                            <strong style={{ color: '#ffeb3b' }}>Special Instructions:</strong>
                            <p style={{ margin: '0.25rem 0 0' }}>{order.special_instructions}</p>
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0',
                          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <h4 style={{ margin: 0 }}>Total:</h4>
                          <h4 style={{ margin: 0, color: 'var(--gold)' }}>£{order.total_amount}</h4>
                        </div>

                        <button
                          onClick={() => sendCustomerNotification(order.id)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'transparent',
                            border: '1px solid var(--gold)',
                            color: 'var(--gold)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          💬 Send Message to Kitchen
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Past Orders */}
            {selectedTab === 'past' && (
              <>
                {pastOrders.length === 0 ? (
                  <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                    <h3 style={{ color: 'var(--gold)' }}>No past orders</h3>
                    <p style={{ opacity: 0.6, marginTop: '0.5rem' }}>
                      Your order history will appear here
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {pastOrders.map(order => (
                      <div key={order.id} className="card" style={{ padding: '1.5rem', opacity: 0.9 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                          <div>
                            <h3 style={{ color: 'var(--gold)', margin: '0 0 0.5rem 0' }}>
                              Order #{order.order_number}
                            </h3>
                            <p style={{ margin: '0 0 0.25rem 0', opacity: 0.7 }}>
                              Table {order.table_number}
                            </p>
                            <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
                              {new Date(order.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div style={{
                            padding: '0.5rem 1rem',
                            background: getStatusColor(order.status),
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            fontSize: '0.85rem'
                          }}>
                            {order.status}
                          </div>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                          {order.order_items.map(item => (
                            <div key={item.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{item.quantity}x {item.menu_item?.name}</span>
                                <span>£{(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.75rem 0',
                          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          <strong>Total:</strong>
                          <strong style={{ color: 'var(--gold)' }}>£{order.total_amount}</strong>
                        </div>

                        <button
                          onClick={() => navigate('/restaurant-order')}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'var(--gold)',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                        >
                          Order Again
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default RestaurantOrders;
